import { useForm } from 'react-hook-form';
import {
  any,
  applySpec,
  assoc,
  compose,
  concat,
  find,
  includes,
  isEmpty,
  map,
  not,
  pathEq,
  pathOr,
  prop,
  propEq,
  propOr,
  reduce
} from 'ramda';
import { isNilOrEmpty } from '@@serverHelpers/index.mjs';
import { useEffect, useState } from 'react';
import { ProjectStatusEnum } from '@fasstech/oip-core/entities/enums';
import { SignatureModeEnum } from '@fasstech/oip-core/entities/Signature/enums';
import { extraMessageErrorSaveDocument } from '@@viewCommonComponents/displayer/extra/extraMessageErrorSaveDocument.js';
import { TaskSubscription } from '../../../_common/client/graphql/subscriptions/TaskSubscription.js';
import { useSubscription } from '@apollo/client';
import { signatureStepEnum } from '@@viewEnums';
import useSaveSouscriptionSupportingDocumentsMutation from './useSaveSouscriptionSupportingDocumentsMutation';
import useDeleteDocumentSouscriptionSupportingDocumentsMutation
  from './useDeleteDocumentSouscriptionSupportingDocumentsMutation';
import {
  extraMessageErrorSendForSignature
} from '@@viewCommonComponents/displayer/extra/extraMessageErrorSendForSignature.js';

const useSouscriptionSupportingDocumentsForm = ({
  data,
  context,
  processHandlers
}) => {
  const [loading, setLoading] = useState(false);

  const [
    errorSendForSignatureModalMessage,
    setErrorSendForSignatureModalMessage
  ] = useState('');
  const [errorSaveDocumentModalMessage, setErrorSaveDocumentModalMessage] =
    useState('');

  const icons = [
    {
      filesType: 'IDENTITY_PROOF',
      icon: 'idCard'
    },
    {
      filesType: 'PRELEVEMENT',
      icon: 'landmark'
    },
    {
      filesType: 'REMBOURSEMENT',
      icon: 'handCoins'
    },
    {
      filesType: 'MANDATORY_REGIME_CERTIFICATE',
      icon: 'fileCheck'
    },
    {
      filesType: 'SCHOOL_CERTIFICATE',
      icon: 'graduationCap'
    },
    {
      filesType: 'HEALTH_INSURANCE_TERMINATION_CERTIFICATE',
      icon: 'shieldCheck'
    },
    {
      filesType: 'KBIS_OR_SIREN',
      icon: 'fileCheck'
    },
    {
      filesType: 'MEMBERSHIP_CERTIFICATE',
      icon: 'fileCheck'
    },
    {
      filesType: 'TNS_CERTIFICATE',
      icon: 'fileCheck'
    },
    {
      filesType: 'CANCELLATION_LETTER',
      icon: 'fileCheck'
    },
    {
      filesType: 'AFFILIATION_CERTIFICATE',
      icon: 'fileCheck'
    }
  ];

  const {
    filesToUpload,
    maxFileSize,
    uploadedFileLimit
  } = applySpec({
    maxFileSize: propOr(0, 'maxFileSize'),
    uploadedFileLimit: propOr(0, 'uploadedFileLimit'),
    filesToUpload: compose(
      map((file) =>
        assoc(
          'icon',
          propOr(
            null,
            'icon',
            find(propEq('filesType', file.filesType), icons)
          ),
          file
        )
      ),
      propOr([], 'filesToUpload')
    )
  })(data);

  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { uploads: filesToUpload }
  });

  const { isValid } = form.formState;

  useEffect(() => {
    form.trigger();
  }, []);

  const [signatureStarted, setSignatureStarted] = useState(
    includes(data.projectStatus, [
      ProjectStatusEnum.SENT_FOR_SIGNATURE,
      ProjectStatusEnum.SIGNED
    ])
  );
  const formData = form.watch();

  const documents = propOr([], 'uploads', formData);

  const hasUploadedFileInGroup = compose(
    find(compose(not, isEmpty, prop('files'))),
    propOr([], 'group')
  );

  const isDocBlocking = (doc) =>
    prop('required', doc) &&
    (!isEmpty(propOr([], 'group', doc))
      ? !hasUploadedFileInGroup(doc)
      : compose(isEmpty, propOr([], 'files'))(doc));

  const documentInGroupIsRequired = any(isDocBlocking, documents);

  const { onFormSubmit: saveDocuments } =
    useSaveSouscriptionSupportingDocumentsMutation();

  const { onFormSubmit: deleteDocuments } =
    useDeleteDocumentSouscriptionSupportingDocumentsMutation();
  const $onPrevClick = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const taskData = {
    taskName: 'sendForSignature',
    projectId: context?.projectId,
    metadata: {
      step: signatureStepEnum.SUBSCRIPTION
    }
  };

  const isElectronicSignature = pathEq(
    ['signature', 'mode'],
    SignatureModeEnum.ELECTRONIC,
    data
  );

  const onSubmit = (values, bypass = false) => {
    if (loading) return;
    setLoading(true);

    const getAllFilesReducer = (acc, item) => {
      if (!isNilOrEmpty(item.files)) {
        acc = concat(acc, item.files);
      }
      if (!isNilOrEmpty(item.group)) {
        acc = concat(acc, reduce(getAllFilesReducer, [], item.group));
      }
      return acc;
    };

    const documents = reduce(
      getAllFilesReducer,
      [],
      propOr([], 'uploads', values)
    );

    const cleanedDocuments = documents.map((doc) => ({
      uploadId: doc.uploadId,
      fileName: doc.fileName,
      fileType: doc.fileType
    }));

    saveDocuments(
      {
        documents: cleanedDocuments,
        bypass
      },
      (ok, err) => {
        if (ok && !err) {
          processHandlers.onKillCronTask(taskData);
          processHandlers.onRunTask(taskData);
        } else {
          setLoading(false);
          setErrorSaveDocumentModalMessage(
            extraMessageErrorSaveDocument(context)
          );
        }
      }
    );
  };

  useSubscription(TaskSubscription, {
    variables: taskData,
    onData: ({ data }) => {
      const status = pathOr(
        {},
        ['data', 'taskSubscription', 'state', 'status'],
        data
      );

      if (status === 'ERROR') {
        setLoading(false);
        setErrorSendForSignatureModalMessage(extraMessageErrorSendForSignature);
      } else if (status === 'DONE') {
        setSignatureStarted(true);
      }
    }
  });

  return {
    form,
    $onPrevClick,
    onSubmit,
    deleteDocuments,
    documents,
    maxFileSize,
    uploadedFileLimit,
    loading,
    signatureStarted,
    isElectronicSignature,
    errorSendForSignatureModalMessage,
    setErrorSendForSignatureModalMessage,
    errorSaveDocumentModalMessage,
    setErrorSaveDocumentModalMessage,
    isValid: isValid && !documentInGroupIsRequired
  };
};

export default useSouscriptionSupportingDocumentsForm;
